import * as XLSX from "xlsx";
import moment from "moment";
import { saveAs } from "file-saver";
import { fetchData } from "../../../services/apiService";

// POST /v481/report/transactions-export (gateway: /api-gateway/v481/transactions-export).
export const TRANSACTION_EXPORT_ENDPOINT = "/transactions-export";
export const TRANSACTION_EXPORT_PAGE_SIZE = 1000;
export const TRANSACTION_EXPORT_MAX_RANGE_DAYS = 31;
const TRANSACTION_EXPORT_MAX_PAGES = 200;

const NUMBER_COLUMNS = new Set([
  "amount",
  "cryptoAmount",
  "exchangeRate",
  "feeAmount",
  "netAmount",
]);

// Local calendar days -> UTC ISO bounds; the end is exclusive (start of the day after `toDate`).
export const localRangeToUtc = (fromDate, toDate) => ({
  dateFrom: moment(fromDate, "YYYY-MM-DD").startOf("day").toISOString(),
  dateTo: moment(toDate, "YYYY-MM-DD").add(1, "day").startOf("day").toISOString(),
});

export const validateExportRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) return "Select both dates.";
  const from = moment(fromDate, "YYYY-MM-DD");
  const to = moment(toDate, "YYYY-MM-DD");
  if (!from.isValid() || !to.isValid()) return "Invalid date.";
  if (to.isBefore(from)) return "The end date must be on or after the start date.";
  if (to.diff(from, "days") + 1 > TRANSACTION_EXPORT_MAX_RANGE_DAYS) {
    return `The range can be at most ${TRANSACTION_EXPORT_MAX_RANGE_DAYS} days.`;
  }
  return null;
};

export async function fetchAllExportRows({
  fromDate,
  toDate,
  merchantID,
  terminalID,
  isoID,
  token,
  acquirerName,
  acquirerID,
}) {
  const { dateFrom, dateTo } = localRangeToUtc(fromDate, toDate);
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    acquirerName,
    acquirerID,
  };
  let columns = [];
  const rows = [];
  let cursor = null;
  for (let page = 0; page < TRANSACTION_EXPORT_MAX_PAGES; page += 1) {
    const data = {
      dateFrom,
      dateTo,
      limit: TRANSACTION_EXPORT_PAGE_SIZE,
      ...(isoID?.trim() ? { isoID: isoID.trim() } : {}),
      ...(merchantID?.trim() ? { merchantID: merchantID.trim() } : {}),
      ...(terminalID?.trim() ? { terminalID: terminalID.trim() } : {}),
      ...(cursor ? { cursor } : {}),
    };
    const response = await fetchData({
      endpoint: TRANSACTION_EXPORT_ENDPOINT,
      data,
      headers,
      token,
    });
    const body = response?.data || {};
    columns = body.columns || columns;
    rows.push(...(body.transactions || []));
    cursor = body.nextCursor;
    if (!cursor) break;
  }
  return { columns, rows, truncated: Boolean(cursor) };
}

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const buildExportCsv = (columns, rows) =>
  [
    columns.join(","),
    ...rows.map((row) => columns.map((c) => csvCell(row[c])).join(",")),
  ].join("\n") + "\n";

export function buildExportSheet(columns, rows) {
  const data = rows.map((row) =>
    columns.reduce((acc, c) => {
      const value = row[c] ?? "";
      const asNumber = NUMBER_COLUMNS.has(c) && value !== "" ? Number(value) : NaN;
      acc[c] = Number.isFinite(asNumber) ? asNumber : value;
      return acc;
    }, {}),
  );
  return XLSX.utils.json_to_sheet(data, { header: columns });
}

export function saveExportFile({ columns, rows, format, fromDate, toDate }) {
  const name = `blokko-transactions-${fromDate.replace(/-/g, "")}-${toDate.replace(/-/g, "")}`;
  if (format === "csv") {
    const blob = new Blob([buildExportCsv(columns, rows)], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `${name}.csv`);
    return;
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildExportSheet(columns, rows), "Transactions");
  XLSX.writeFile(wb, `${name}.xlsx`);
}
